// A table of players' numbers (B-412, B-517, B-518): each player's position's
// own columns under a range — the season and the last three games a game's
// average (B-520), last week, the projection — and, before them, whatever
// columns the screen leads with: Players' fantasy columns (points, points a
// game, projection, opponent), My Team's slot and games. Players draws one
// for the position chosen, or with every position its fantasy columns alone;
// My Team one for each position of its roster.
//
// THE NAME STAYS, THE NUMBERS SCROLL. The player's column is fixed at the
// left and the rest scroll sideways under it when they are wider than the
// box (a phone, a large text size, Everything's many columns), the way
// ESPN's and Yahoo's tables do on a phone; the head stays at the top
// (`TableScroll`, UX-36). A row's action, when it has one at the end, stays
// at the right edge the same way, so Add is in reach wherever the numbers
// have scrolled to.
//
// A DASH IS NO GAME, A ZERO IS A ZERO. A row with no games in the range reads
// a dash in every column; a player who played and caught nothing reads 0
// (`RowStatsView`). A rate over nothing (yards a carry with no carries) is a
// dash too: there is no number to give.

import type { ReactNode } from 'react';
import type { PlayerSummary, RowStatsView, StatColumnView, StatRange } from '@fsp/domain';
import type { DetailLevel } from '@fsp/theme';
import { PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import { SortHead, type SortFirst } from '../../kit/SortHead';
import { TableScroll } from '../../kit/TableScroll';
import { Term } from '../../kit/Why';
import { num } from './notComputed';
import '../../styles/screens/stats-table.css';

/** The stats' ranges, in the order a manager reaches for them. */
export const STAT_RANGES: readonly { value: StatRange; label: string }[] = [
  { value: 'season', label: 'Season' },
  { value: 'recent', label: 'Last 3 games' },
  { value: 'week', label: 'Last week' },
  { value: 'projected', label: 'This week, projected' },
];

/** A range's name in the menu: last week by its own once a read has said which it was. */
export function rangeLabel(
  r: { value: StatRange; label: string },
  week: { label: string } | null,
): string {
  return r.value === 'week' && week !== null ? `Last week, ${week.label}` : r.label;
}

/** What the points column holds under a range, said when asked. */
export function rangePointsTip(range: StatRange, week: { label: string } | null): string {
  switch (range) {
    case 'season':
      return 'His fantasy points this season, in this league’s scoring.';
    case 'recent':
      return 'His fantasy points over his last three games.';
    case 'week':
      return `His fantasy points in ${week?.label ?? 'the last week that has ended'}.`;
    case 'projected':
      return 'His projected fantasy points this week.';
  }
}

/**
 * A position's columns at a reader's detail level (B-517): Simple's alone,
 * Standard's beside them, every one at Everything — the order the sport's
 * sheet gives them in, whatever the level leaves out.
 */
export function columnsAt<C extends { level: StatColumnView['level'] }>(
  columns: readonly C[],
  level: DetailLevel,
): C[] {
  return columns.filter(
    (c) =>
      level === 'pro' || c.level === 'simple' || (level === 'standard' && c.level !== 'everything'),
  );
}

/**
 * A column's number as it reads: made of tried (*18/21*) for a made-of
 * column, its own places for a rate (*4.0*), a whole count whole and a
 * projection's to a tenth; a dash where there is nothing to give.
 */
export function statCellText(
  c: Pick<StatColumnView, 'key' | 'decimals' | 'attempts'>,
  values: Readonly<Record<string, number>> | null | undefined,
): string {
  const v = values?.[c.key];
  if (v === undefined) return '—';
  const text = (n: number) =>
    c.decimals > 0
      ? n.toFixed(c.decimals)
      : Number.isInteger(n)
        ? n.toLocaleString('en-US')
        : n.toFixed(1);
  if (c.attempts !== null) {
    const tried = values?.[c.attempts];
    return tried === undefined ? text(v) : `${text(v)}/${text(tried)}`;
  }
  return text(v);
}

export interface StatsTableRow {
  key: string;
  player: PlayerSummary;
  stats: RowStatsView | null;
  /** Before the position: a lineup's slot. */
  lead?: ReactNode;
  /** After his club and status, on the name's line: the watch star. */
  marks?: ReactNode;
  /** Under the name: who holds him, a waiver's clearing, why nothing can be done. */
  sub?: ReactNode;
  /** The row's action: in the fixed column, or at the end with `actionAtEnd`. */
  action?: ReactNode;
  /** Ticked to compare (B-375). */
  picked?: boolean;
  /** Opens his card: the name's press, and the row's. */
  onOpen: () => void;
}

/** A column a screen leads its stats with: a figure, its head, and each row's cell. */
export interface LeadColumn<R extends StatsTableRow> {
  key: string;
  /** The head's short label. */
  label: string;
  /** What it holds, said when asked. */
  tip: string;
  /** The order it sorts by, when its head sorts; absent, a plain head. */
  sort?: { on: boolean; reverse: boolean; first?: SortFirst; onSort: () => void };
  cell: (row: R) => ReactNode;
  /** The figure the list is in order by, strong. */
  strong?: boolean;
  /** Words rather than a number, its head and cells left as words read: the opponent. */
  words?: boolean;
}

export function StatsTable<R extends StatsTableRow>({
  label,
  rows,
  leads = [],
  columns,
  games,
  points = true,
  pointsTip = '',
  sort,
  short = false,
  actionAtEnd = false,
  actionHead,
  pill = true,
}: {
  /** The first column's head: "312 players", "Starters". */
  label: string;
  rows: readonly R[];
  /** The screen's own columns before the stats: Players' fantasy columns. */
  leads?: readonly LeadColumn<R>[];
  /** The stats' columns, already at the reader's level (`columnsAt`). */
  columns: readonly StatColumnView[];
  /** A games column before the stats: the season's and the last three's. */
  games: boolean;
  /** The range's points last, as My Team's tables end; Players leads with them instead. */
  points?: boolean;
  /** What the points column holds, said when asked. */
  pointsTip?: string;
  /**
   * The order, when the stats' heads sort it: a stat's key, or null for the
   * points; `on` false while the list is in some other order.
   */
  sort?: {
    stat: string | null;
    reverse: boolean;
    on?: boolean;
    onSort: (stat: string | null) => void;
  };
  /**
   * Short names ("J.Gibbs"), for a phone's fixed column, where a whole name
   * left a few letters; the button's name says the whole one after it.
   */
  short?: boolean;
  /** The action in a column of its own at the right edge, rather than beside the name. */
  actionAtEnd?: boolean;
  /**
   * Each row's position pill; off for a list of one position, which its
   * chip already names, so a phone's fixed column is the name's.
   */
  pill?: boolean;
  /** That column's head: "Compare" while picking. */
  actionHead?: string;
}) {
  const statsOn = sort !== undefined && sort.on !== false;
  const head = (stat: string | null, text: string, tip: string) =>
    sort === undefined ? (
      <Term tip={tip} name={text}>
        {text}
      </Term>
    ) : (
      <SortHead
        label={text}
        tip={tip}
        on={statsOn && sort.stat === stat}
        reverse={sort.reverse}
        onSort={() => sort.onSort(stat)}
      />
    );
  return (
    <TableScroll className="fsp-stats-box">
      <table className="fsp-sheet fsp-stats">
        <thead>
          <tr>
            <th scope="col" className="fsp-stats-who">
              {label}
            </th>
            {leads.map((l) => (
              <th key={l.key} scope="col" className={l.words ? 'fsp-stats-words' : 'fsp-sheet-num'}>
                {l.sort === undefined ? (
                  <Term tip={l.tip} name={l.label}>
                    {l.label}
                  </Term>
                ) : (
                  <SortHead
                    label={l.label}
                    tip={l.tip}
                    on={l.sort.on}
                    reverse={l.sort.reverse}
                    first={l.sort.first}
                    onSort={l.sort.onSort}
                  />
                )}
              </th>
            ))}
            {games ? (
              <th scope="col" className="fsp-sheet-num">
                <Term tip="His games in these weeks." name="Games">
                  G
                </Term>
              </th>
            ) : null}
            {columns.map((c) => (
              <th key={c.key} scope="col" className="fsp-sheet-num">
                {head(c.key, c.label, c.name)}
              </th>
            ))}
            {points ? (
              <th scope="col" className="fsp-sheet-num fsp-stats-pts">
                {head(null, 'Pts', pointsTip)}
              </th>
            ) : null}
            {actionAtEnd ? (
              <th scope="col" className="fsp-stats-act">
                {actionHead ?? <span className="fsp-sr">Action</span>}
              </th>
            ) : null}
            {/* The room a wide window leaves, after the row's numbers and its
                action (B-520). */}
            <td className="fsp-stats-spacer" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const played = r.stats !== null && r.stats.games > 0;
            return (
              <tr
                key={r.key}
                className="fsp-row-clickable"
                data-ux-convenience=""
                data-picked={r.picked || undefined}
                onClick={r.onOpen}
              >
                <th scope="row" className="fsp-stats-who">
                  <span className="fsp-stats-who-in">
                    {r.lead}
                    {pill ? <PositionPill pos={r.player.positions[0]!} size="sm" /> : null}
                    <span className="fsp-stats-who-text">
                      <span className="fsp-stats-name">
                        <button
                          type="button"
                          className="fsp-linklike fsp-name-text fsp-name-btn"
                          // The words on screen first, so a voice command
                          // that reads them reaches it (WCAG 2.5.3, B-392).
                          aria-label={short ? `${r.player.shortName}, ${r.player.name}` : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            r.onOpen();
                          }}
                        >
                          {short ? r.player.shortName : r.player.name}
                        </button>
                        <ProTeamCode code={r.player.proTeam} />
                        <StatusBadge player={r.player} />
                        {r.marks}
                      </span>
                      {r.sub !== undefined && r.sub !== null ? (
                        <span className="fsp-stats-sub fsp-sub">{r.sub}</span>
                      ) : null}
                    </span>
                    {r.action !== undefined && !actionAtEnd ? (
                      // Keeps the row's own press off the action's.
                      <span
                        className="fsp-stats-action"
                        data-ux-stop=""
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.action}
                      </span>
                    ) : null}
                  </span>
                </th>
                {leads.map((l) => (
                  <td
                    key={l.key}
                    className={`${l.words ? 'fsp-stats-words' : 'fsp-sheet-num fsp-num'}${l.strong ? ' fsp-stats-strong' : ''}`}
                  >
                    {l.cell(r)}
                  </td>
                ))}
                {games ? (
                  <td className="fsp-sheet-num fsp-num">{played ? r.stats!.games : '—'}</td>
                ) : null}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`fsp-sheet-num fsp-num${statsOn && sort.stat === c.key ? ' fsp-stats-strong' : ''}`}
                  >
                    {played ? statCellText(c, r.stats!.values) : '—'}
                  </td>
                ))}
                {points ? (
                  <td className="fsp-sheet-num fsp-num fsp-stats-pts">
                    {played ? num(r.stats!.points) : '—'}
                  </td>
                ) : null}
                {actionAtEnd ? (
                  // Keeps the row's own press off the action's.
                  <td
                    className="fsp-stats-act"
                    data-ux-stop=""
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.action}
                  </td>
                ) : null}
                <td className="fsp-stats-spacer" aria-hidden="true" />
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}
